// Lógica PURA del avance del equipo en Usuarios (tanda «Aprende Dashbo», frente apr-fe-equipo).
// Sin React y sin i18n: lo que se dibuja lo decide el componente; acá vive qué se puede decir de
// cada persona con lo que manda `GET learning/team/`.
//
// Reglas de la sección 7 del mockup aprobado (que no se sienta vigilancia):
//  - avance y logros, nunca contenido: cuántas consultas, nunca cuáles;
//  - sin ranking POR DEFECTO: el orden de las filas es el de `agency/team`, nunca por avance. El
//    Admin ordena a pedido tocando un encabezado (card Ni3pOCBx, fede 08/10): quiere ver quiénes
//    usan más la IA, pero la tabla no abre como un ranking;
//  - las columnas viajan sólo para Admin, y eso lo decide el backend (404 para el resto).

import {parsePanelDate} from '../../client/panel_v11/panelFormatters';

// Una semana en HORAS, no en días de calendario: el backend corta las dos ventanas con
// `_days_ago(7)` = ahora − 168 h (review C-apr-fe-equipo-06). Con días de calendario, en una franja
// de horas la celda salía en ámbar sin que el chip «sin entrar hace 7 días» la contara, y «Recordarle»
// reaparecía para recibir un `NUDGE_TOO_SOON`.
export const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

function msSince(value, now) {
    const date = parsePanelDate(value);
    return date ? now.getTime() - date.getTime() : null;
}

/** Los miembros de `learning/team/` por `user_id`. Quien no está (portal, superadmin) no tiene avance. */
export function membersByUserId(learning) {
    const map = new Map();
    ((learning && learning.members) || []).forEach((member) => map.set(member.user_id, member));
    return map;
}

/** Las invitaciones de equipo de `learning/team/` por email, sin distinguir mayúsculas. */
export function invitationsByEmail(learning) {
    const map = new Map();
    ((learning && learning.invitations) || []).forEach((invitation) => {
        if (invitation && invitation.email) map.set(invitation.email.toLowerCase(), invitation);
    });
    return map;
}

export function invitationFor(invitationsMap, email) {
    return (email && invitationsMap.get(email.toLowerCase())) || null;
}

/** 0–100, entero. Un total en cero (pista sin tareas que apliquen) da 0, no NaN. */
export function progressPercent(progress) {
    const total = Number(progress && progress.total) || 0;
    const done = Number(progress && progress.done) || 0;
    if (total <= 0) return 0;
    return Math.max(0, Math.min(100, Math.round((done / total) * 100)));
}

/**
 * Sin entrar hace una semana o más, o nunca: lo mismo que cuenta `summary.inactive_7d`
 * (`último uso <= ahora − 168 h`).
 */
export function isInactive(lastActivityAt, now = new Date()) {
    const elapsed = msSince(lastActivityAt, now);
    return elapsed === null || elapsed >= WEEK_MS;
}

/**
 * ¿Ya se le recordó dentro del tope? Entonces la fila dice cuándo, no ofrece el botón. Mismo borde que
 * el backend (`creado >= ahora − 168 h` es «muy pronto»).
 */
export function remindedRecently(nudgedAt, now = new Date()) {
    const elapsed = msSince(nudgedAt, now);
    return elapsed !== null && elapsed <= WEEK_MS;
}

/**
 * «Recordarle» va donde hay algo concreto que recordar: le falta algo de lo esencial (el aviso
 * nombra su próxima tarea). Nunca a uno mismo.
 */
export function canRemind(member, currentUserId) {
    if (!member) return false;
    if (currentUserId != null && String(member.user_id) === String(currentUserId)) return false;
    return (member.pending_essentials || []).length > 0;
}

/** ¿La fila tiene algo que mostrar al expandirse? */
export function hasDetail(member) {
    if (!member) return false;
    return (member.pending_essentials || []).length > 0
        || (member.manual_done || []).length > 0
        || !!member.last_progress_at;
}

/**
 * El valor por el que se ordena una persona en cada columna de avance (card Ni3pOCBx). `null` = no
 * tiene ese dato (no eligió perfil, nunca entró, no es del equipo) y `sortRows` la manda al final en
 * las dos direcciones: no tenerlo no es «tener el menor».
 */
export function memberSortValue(member, column) {
    if (!member) return null;
    switch (column) {
        case 'lastUse': {
            const date = parsePanelDate(member.last_activity_at);
            return date ? date.getTime() : null;
        }
        case 'essentials':
            return progressPercent(member.essentials);
        case 'profile': {
            if (!member.profile_chosen) return null;
            const tracks = member.profile_tracks || [];
            const total = tracks.reduce((sum, track) => sum + (Number(track.total) || 0), 0);
            const done = tracks.reduce((sum, track) => sum + (Number(track.done) || 0), 0);
            return total > 0 ? Math.round((done / total) * 100) : 0;
        }
        case 'achievements':
            return (member.achievements || []).length;
        case 'aiQueries':
            return Number(member.ai_queries_30d) || 0;
        default:
            return null;
    }
}

const twoDigits = (n) => String(n).padStart(2, '0');

/**
 * `04/10`: la fecha corta de «Último avance», «Recordado el» e «Invitación enviada el», en el día
 * local. Armada a mano y no con `toLocaleDateString('es-AR', {day: '2-digit'})`: el ICU de Node
 * devuelve «4/10» con esas mismas opciones, así que el formato cambiaría según el motor.
 */
export function shortDate(value) {
    const date = parsePanelDate(value);
    if (!date) return null;
    return `${twoDigits(date.getDate())}/${twoDigits(date.getMonth() + 1)}`;
}
